// react-redux imports
import { MouseEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";

// styled-components imports
import CylinderIndication from "./styled-components/CylinderIndication";
import UserInputSection from "../styled-components/UserInputSection";

import { CYLINDER_OPTIONS } from "../constants/energy";

// common imports
import CircleButton from "common/components/CircleButton";
import CircleButtonContainer from "common/components/styled-components/CircleButtonContainer";
import CircleButtonGroupWithLabel from "common/components/styled-components/CircleButtonGroupWithLabel";
import InputGroupLabel from "common/components/styled-components/InputGroupLabel";
import SectionTitle from "common/components/styled-components/SectionTitle";
import SectionTitleIndication from "common/components/styled-components/SectionTitleIndication";
import Tooltip from "common/components/Tooltip";
import TooltipPopUp from "common/components/TooltipPopUp";
import { TooltipAction, TOOLTIP_ACTIONS } from "common/constants/tooltip";

import { setButaneUseMonthly, setPropaneUseMonthly } from "./energySlice";
import PopUpParagraph from "common/components/styled-components/PopUpParagraph";

interface Tooltips {
  butane: boolean;
  propane: boolean;
}

const initialTooltipsState: Tooltips = {
  butane: false,
  propane: false,
};

function StepThree() {
  const [tooltips, setTooltips] = useState<Tooltips>(initialTooltipsState);
  const dispatch = useDispatch();
  const upDesktopScreen = useSelector(
    (state: RootState) => state.app.upDesktopScreen
  );

  const butaneUseMonthly = useSelector(
    (state: RootState) => state.calculator.energy.butaneUseMonthly
  );
  const propaneUseMonthly = useSelector(
    (state: RootState) => state.calculator.energy.propaneUseMonthly
  );

  function handleButaneSelection(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const value = Number(e.currentTarget.value);
    if (value === butaneUseMonthly) dispatch(setButaneUseMonthly(0));
    else dispatch(setButaneUseMonthly(value));
  }

  function handlePropaneSelection(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const value = Number(e.currentTarget.value);
    if (value === propaneUseMonthly) dispatch(setPropaneUseMonthly(0));
    else dispatch(setPropaneUseMonthly(value));
  }

  function handleTooltip(tooltip: keyof Tooltips, action: TooltipAction) {
    switch (action) {
      case TOOLTIP_ACTIONS.OPEN:
        return () => setTooltips({ ...initialTooltipsState, [tooltip]: true });

      case TOOLTIP_ACTIONS.CLOSE:
        return () => setTooltips(initialTooltipsState);

      case TOOLTIP_ACTIONS.TOGGLE:
        return () =>
          setTooltips({
            ...initialTooltipsState,
            [tooltip]: !tooltips[tooltip],
          });

      default:
        return () => undefined;
    }
  }

  return (
    <>
      <SectionTitle>
        <span>¿Cuál es tu consumo medio de estas energías? </span>
        <SectionTitleIndication>(nº bombona/mes)</SectionTitleIndication>
        {tooltips.butane && (
          <TooltipPopUp
            title="Butano"
            handleClose={handleTooltip("butane", TOOLTIP_ACTIONS.CLOSE)}
          >
            <PopUpParagraph>
              Las bombonas de butano se utilizan para las calderas, los
              calentadores, los radiadores, estufas o cocinas.
            </PopUpParagraph>
          </TooltipPopUp>
        )}
        {tooltips.propane && (
          <TooltipPopUp
            title="Propano"
            handleClose={handleTooltip("propane", TOOLTIP_ACTIONS.CLOSE)}
          >
            <PopUpParagraph>
              El propano es una energía que suele emplearse tanto en los hogares
              usándose principalmente para la calefacción, el calentamiento del
              agua y la cocina.
            </PopUpParagraph>
          </TooltipPopUp>
        )}
      </SectionTitle>
      <UserInputSection>
        <CircleButtonGroupWithLabel gap={upDesktopScreen ? 20 : 8}>
          <InputGroupLabel>
            <span>Bombonas de butano </span>
            <CylinderIndication>(1 = 12Kg)&nbsp;</CylinderIndication>
            <Tooltip
              size={17}
              onClick={handleTooltip("butane", TOOLTIP_ACTIONS.TOGGLE)}
            />
          </InputGroupLabel>
          <CircleButtonContainer>
            {CYLINDER_OPTIONS.map((cylinder) => (
              <CircleButton
                key={`butane__${cylinder.value}`}
                text={cylinder.label}
                value={cylinder.value}
                selected={cylinder.value === butaneUseMonthly}
                onClick={handleButaneSelection}
              />
            ))}
          </CircleButtonContainer>
        </CircleButtonGroupWithLabel>
        <CircleButtonGroupWithLabel gap={upDesktopScreen ? 20 : 8}>
          <InputGroupLabel>
            <span>Propano </span>
            <CylinderIndication>(1 = 11Kg)&nbsp;</CylinderIndication>
            <Tooltip
              size={17}
              onClick={handleTooltip("propane", TOOLTIP_ACTIONS.TOGGLE)}
            />
          </InputGroupLabel>
          <CircleButtonContainer>
            {CYLINDER_OPTIONS.map((cylinder) => (
              <CircleButton
                key={`propane__${cylinder.value}`}
                text={cylinder.label}
                value={cylinder.value}
                selected={cylinder.value === propaneUseMonthly}
                onClick={handlePropaneSelection}
              />
            ))}
          </CircleButtonContainer>
        </CircleButtonGroupWithLabel>
      </UserInputSection>
    </>
  );
}

export default StepThree;
